const BiopharmaceuticsUnit3Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit III: Pharmacokinetics & Compartment Models</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            <strong>Pharmacokinetics (PK)</strong> is the mathematics of the time course of Absorption, Distribution, Metabolism, and Excretion (ADME) of drugs in the body. It essentially describes "what the body does to the drug" mathematically.
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Introduction to Pharmacokinetic Models</h3>
        <p class="text-[13px] text-slate-700 mb-4">Because the human body is highly complex, we use simplified mathematical models to predict drug concentration in plasma and tissues over time.</p>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-blue-50 p-4 border border-blue-100 rounded-lg">
                <h4 class="font-bold text-blue-900 text-[14px] mb-2">A. Compartment Models</h4>
                <p class="text-[12px] text-slate-600 mb-2">The body is represented as a series of interconnected compartments (tanks). It assumes rapid and uniform distribution within a compartment.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-700">
                    <li><strong>One-Compartment:</strong> Whole body acts as one single unit.</li>
                    <li><strong>Two-Compartment:</strong> Body divided into Central (blood/highly perfused organs) and Peripheral (muscles/fat) compartments.</li>
                </ul>
            </div>
            
            <div class="bg-blue-50 p-4 border border-blue-100 rounded-lg">
                <h4 class="font-bold text-blue-900 text-[14px] mb-2">B. Non-Compartment Models</h4>
                <p class="text-[12px] text-slate-600 mb-2">Also known as Model-independent approach. It does not assume any specific compartments.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-700">
                    <li>Based on <strong>Statistical Moments Theory</strong>.</li>
                    <li>Uses parameters like Mean Residence Time (MRT) and Area Under the Curve (AUC).</li>
                    <li>Highly useful for clinical applications and BA/BE studies.</li>
                </ul>
            </div>

            <div class="bg-blue-50 p-4 border border-blue-100 rounded-lg">
                <h4 class="font-bold text-blue-900 text-[14px] mb-2">C. Physiological Models</h4>
                <p class="text-[12px] text-slate-600 mb-2">Also known as Perfusion-based models. These are realistic models based on actual anatomical and physiological data.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-700">
                    <li>Considers actual blood flow (perfusion rate) to individual organs.</li>
                    <li>Very complex and mathematically difficult, but highly accurate for species-to-species extrapolation.</li>
                </ul>
            </div>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">2. Crucial Pharmacokinetic Parameters</h3>
        
        <div class="overflow-x-auto rounded-lg border border-slate-200">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-slate-100 text-slate-700">
                    <tr>
                        <th class="px-4 py-3 font-bold w-1/5">Parameter</th>
                        <th class="px-4 py-3 font-bold w-1/2">Definition & Significance</th>
                        <th class="px-4 py-3 font-bold w-1/4 text-center">Formula</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-700">
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-800">K<sub>E</sub> (Elimination Rate Constant)</td>
                        <td class="px-4 py-3">The fraction of the drug present in the body that is eliminated per unit time. Represents overall elimination (metabolism + excretion).</td>
                        <td class="px-4 py-3 font-mono text-[11px] text-center bg-slate-50">K<sub>E</sub> = 0.693 / t<sub>1/2</sub></td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-800">t<sub>1/2</sub> (Biological Half-Life)</td>
                        <td class="px-4 py-3">The time required for the drug concentration in the plasma to reduce to half (50%) of its initial value. Determines dosing frequency.</td>
                        <td class="px-4 py-3 font-mono text-[11px] text-center bg-slate-50">t<sub>1/2</sub> = 0.693 / K<sub>E</sub></td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-800">V<sub>d</sub> (Apparent Volume of Distribution)</td>
                        <td class="px-4 py-3">A theoretical volume in which the total amount of drug administered would need to be uniformly dissolved to match the plasma concentration.</td>
                        <td class="px-4 py-3 font-mono text-[11px] text-center bg-slate-50">V<sub>d</sub> = Dose / C<sub>0</sub></td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-800">AUC (Area Under the Curve)</td>
                        <td class="px-4 py-3">Integral of the plasma drug concentration-time curve. It represents the <strong>total systemic exposure</strong> to the drug. Evaluates Bioavailability.</td>
                        <td class="px-4 py-3 font-mono text-[11px] text-center bg-slate-50">Calculated by Trapezoidal Rule</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-800">K<sub>a</sub> (Absorption Rate Constant)</td>
                        <td class="px-4 py-3">The fractional rate of drug absorption into the systemic circulation. Higher K<sub>a</sub> means faster absorption.</td>
                        <td class="px-4 py-3 font-mono text-[11px] text-center bg-slate-50">Derived from Wagner-Nelson method</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-800">Cl<sub>T</sub> (Total Body Clearance)</td>
                        <td class="px-4 py-3">The hypothetical volume of plasma from which the drug is completely removed (cleared) per unit time. Cl<sub>T</sub> = Cl<sub>Renal</sub> + Cl<sub>Hepatic</sub>.</td>
                        <td class="px-4 py-3 font-mono text-[11px] text-center bg-slate-50">Cl<sub>T</sub> = K<sub>E</sub> × V<sub>d</sub><br>or<br>Cl<sub>T</sub> = Dose / AUC</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="bg-indigo-50 p-6 border border-indigo-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-900 mb-4 border-b border-indigo-200 pb-2">3. One Compartment Open Model</h3>
        <p class="text-[13px] text-slate-700 mb-4">
            The simplest pharmacokinetic model. <br>
            <strong>"One Compartment"</strong> = The body acts as a single, uniform unit where the drug distributes instantly.<br>
            <strong>"Open"</strong> = There is an input (absorption) and a continuous output (elimination/excretion) of the drug. Elimination usually follows <strong>First-Order Kinetics</strong>.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div class="bg-white p-4 rounded-lg border border-indigo-100 shadow-sm">
                <h4 class="text-[15px] font-bold text-indigo-800 mb-2">A. Intravenous (IV) Bolus</h4>
                <p class="text-[12px] text-slate-600 mb-2">The entire dose enters the blood immediately. No absorption phase (K<sub>a</sub> is irrelevant). Drug concentration strictly declines over time.</p>
                <div class="bg-slate-100 p-2 rounded text-center mb-2">
                    <p class="font-mono text-[12px] font-bold text-blue-900">C = C<sub>0</sub> e<sup>-K<sub>E</sub>t</sup></p>
                </div>
                <ul class="list-disc pl-4 text-[11px] text-slate-600">
                    <li><strong>C:</strong> Plasma conc. at time t</li>
                    <li><strong>C<sub>0</sub>:</strong> Initial plasma conc. (at t=0)</li>
                    <li>A plot of log C vs time gives a straight line with slope = -K<sub>E</sub>/2.303</li>
                </ul>
            </div>

            <div class="bg-white p-4 rounded-lg border border-indigo-100 shadow-sm">
                <h4 class="text-[15px] font-bold text-indigo-800 mb-2">B. Intravenous Infusion</h4>
                <p class="text-[12px] text-slate-600 mb-2">Drug is pumped into the blood at a constant rate (Zero-order input) while elimination is First-order.</p>
                <div class="bg-slate-100 p-2 rounded text-center mb-2">
                    <p class="font-mono text-[12px] font-bold text-blue-900">C<sub>ss</sub> = R<sub>0</sub> / (V<sub>d</sub> × K<sub>E</sub>)</p>
                </div>
                <ul class="list-disc pl-4 text-[11px] text-slate-600">
                    <li><strong>C<sub>ss</sub>:</strong> Steady State Concentration (where input rate equals elimination rate).</li>
                    <li><strong>R<sub>0</sub>:</strong> Infusion rate (e.g., mg/hr).</li>
                    <li>Used for maintaining constant therapeutic levels (e.g., normal saline or antibiotics drip).</li>
                </ul>
            </div>

            <div class="bg-white p-4 rounded-lg border border-indigo-100 shadow-sm">
                <h4 class="text-[15px] font-bold text-indigo-800 mb-2">C. Extravascular (Oral) Administration</h4>
                <p class="text-[12px] text-slate-600 mb-2">Involves both Absorption (K<sub>a</sub>) and Elimination (K<sub>E</sub>) phases. The curve goes up (absorption), reaches C<sub>max</sub>, then goes down (elimination).</p>
                <div class="bg-slate-100 p-2 rounded text-center mb-2">
                    <p class="font-mono text-[11px] font-bold text-blue-900">Bateman Equation is used to determine plasma conc. at any given time.</p>
                </div>
                <ul class="list-disc pl-4 text-[11px] text-slate-600">
                    <li><strong>C<sub>max</sub>:</strong> Peak plasma concentration.</li>
                    <li><strong>t<sub>max</sub>:</strong> Time to reach C<sub>max</sub>.</li>
                    <li>The method of residuals (Feathering) is used to separate the absorption phase from the elimination phase.</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default BiopharmaceuticsUnit3Notes;